<script lang="ts" setup>
import InputSelect from '#components/elements/input/inputSelect/InputSelect.vue';
import { useLanguage } from '#composables/useLanguage';
import { C4InputRadio } from '@c4-links/vue3-links-components';
import {
  nielsenPeriodStrToNielsenPeriod,
  nielsenPeriodToStr,
  type DateSelectionOption,
  type DeepPartial,
  type NielsenIntervalType,
  type NielsenPeriodIntervalType,
  type NielsenPeriodType
} from '@carrefour-gcs/shared';
import { computed } from 'vue';
import DateOptionsSelector from '../dateOptionsSelector/DateOptionsSelector.vue';
import {
  generateNielsenPeriodOptions,
  getNielsenCumulativeOptions,
  getUpdatedNielsenIntervalValues
} from '../nielsenPeriod';

const { t } = useLanguage();

const props = defineProps<{
  referentialInterval: NielsenIntervalType;
  selectedPeriod: NielsenPeriodIntervalType;
}>();

const emit = defineEmits<{
  'update-period': [period: NielsenPeriodIntervalType];
}>();

// ⚠️ If this period has a greater interval than the reference period => we'll not have a corresponding year option in the select
//  ex referentialInterval (P012023 -> P132023) and selectedPeriod (P012022 -> P132022)
//  It should happen only on INTEG env
const nielsenIntervalInput = computed(() => {
  return {
    start: nielsenPeriodStrToNielsenPeriod(props.selectedPeriod.periodInterval.start),
    end: nielsenPeriodStrToNielsenPeriod(props.selectedPeriod.periodInterval.end)
  };
});

const periodNielsenOptions = computed(() =>
  generateNielsenPeriodOptions({
    referentialInterval: props.referentialInterval,
    selectedStart: nielsenIntervalInput.value.start,
    selectedEnd: nielsenIntervalInput.value.end
  })
);

const cumulativeOptions = computed(() =>
  getNielsenCumulativeOptions(nielsenPeriodStrToNielsenPeriod(props.referentialInterval.end))
);

const cumulativeRadioOptions = computed(() =>
  Object.values(cumulativeOptions.value).map((option) => ({
    label: `timeSelectorPopin.${option.value}`,
    subLabel: option.label,
    value: option.value
  }))
);

function updateSelectedInterval({ start, end }: DeepPartial<{ start: NielsenPeriodType; end: NielsenPeriodType }>) {
  const values = getUpdatedNielsenIntervalValues({
    start,
    end,
    referentialInterval: props.referentialInterval,
    currentInterval: nielsenIntervalInput.value
  });

  emit('update-period', {
    ...props.selectedPeriod,
    periodInterval: {
      ...props.selectedPeriod.periodInterval,
      start: nielsenPeriodToStr({
        ...nielsenIntervalInput.value.start,
        ...values.start
      }),
      end: nielsenPeriodToStr({ ...nielsenIntervalInput.value.end, ...values.end })
    }
  });
}

function onCheck(option: DateSelectionOption) {
  const updatedPeriod =
    option !== 'dateRange' ? cumulativeOptions.value[option].periodInterval : props.selectedPeriod.periodInterval;

  emit('update-period', {
    ...props.selectedPeriod,
    option,
    periodInterval: updatedPeriod
  });
}

const isDateRangeActive = computed(() => props.selectedPeriod.option === 'dateRange');

const radioInputClx = computed(() => (isChecked: boolean) => ({
  'border-border-main-primary': !isChecked,
  'border-interactive-active-main-primary': isChecked
}));
</script>

<template>
  <div>
    <div class="flex flex-col w-full mt-4">
      <C4InputRadio
        :name="'cumulative-options'"
        :label="t('timeSelectorPopin.customInterval')"
        :checked="isDateRangeActive"
        class="mr-2 font-bold mb-4"
        :class="radioInputClx(isDateRangeActive)"
        value="dateRange"
        data-testid="nielsen-date-range-radio"
        @change="onCheck('dateRange')"
      >
        <div class="grid grid-cols-7 items-center gap-5 w-full py-4">
          <div class="capitalize col-span-1">{{ t(`commonLabel.from`) }}</div>
          <div class="col-span-3">
            <InputSelect
              name="nielsen-start-year-selector"
              label=""
              :model-value="nielsenIntervalInput.start.year"
              :options="periodNielsenOptions.startOptions.years"
              test-id="nielsen-start-year-input"
              @update:model-value="
                (value) => {
                  if (value !== undefined) {
                    updateSelectedInterval({
                      start: { year: value }
                    });
                  }
                }
              "
            />
          </div>
          <div class="col-span-3">
            <InputSelect
              name="nielsen-start-period-selector"
              label=""
              :model-value="nielsenIntervalInput.start.period"
              :options="periodNielsenOptions.startOptions.periods"
              test-id="nielsen-start-period-input"
              @update:model-value="
                (value) => {
                  if (value !== undefined) {
                    updateSelectedInterval({
                      start: { period: value }
                    });
                  }
                }
              "
            />
          </div>

          <div class="capitalize col-span-1">{{ t(`commonLabel.to`) }}</div>
          <div class="col-span-3">
            <InputSelect
              name="nielsen-end-year-selector"
              label=""
              :model-value="nielsenIntervalInput.end.year"
              :options="periodNielsenOptions.endOptions.years"
              test-id="nielsen-end-year-input"
              @update:model-value="
                (value) =>
                  updateSelectedInterval({
                    end: { year: value }
                  })
              "
            />
          </div>
          <div class="col-span-3">
            <InputSelect
              name="nielsen-end-period-selector"
              label=""
              :model-value="nielsenIntervalInput.end.period"
              :options="periodNielsenOptions.endOptions.periods"
              test-id="nielsen-end-period-input"
              @update:model-value="
                (value) =>
                  updateSelectedInterval({
                    end: { period: value }
                  })
              "
            />
          </div>
        </div>
      </C4InputRadio>

      <DateOptionsSelector
        :selected-value="selectedPeriod.option"
        name="cumulative-options"
        :options="cumulativeRadioOptions"
        @check="onCheck($event)"
      />
    </div>
  </div>
</template>
